import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTicketStore } from "../../../store/ticketStore";
import { format } from "date-fns";
import { XMarkIcon, PlusIcon, MinusIcon } from "@heroicons/react/24/outline";

// 모달 컴포넌트
const DailyDetailModal = ({
	ticketItemIdsStr,
	trainNumber,
	departureDate,
	departureStation,
	arrivalStation,
	isOpen,
	onClose,
}) => {
	const {
		ticketDetails,
		isLoading,
		error,
		getSeatAssignments,
		resetTicketDetails,
		getSeatmapLayout,
	} = useTicketStore();

	// 모든 열차 데이터를 저장할 상태 추가
	const [trainData, setTrainData] = useState({});
	const [isNumericSystem, setIsNumericSystem] = useState(false);
	const [totalTeamCounts, setTotalTeamCounts] = useState({});
	const [userSeatNumbers, setUserSeatNumbers] = useState({});
	const [seatmapLayout, setSeatmapLayout] = useState(null);
	const [seatmapLoading, setSeatmapLoading] = useState(false);
	const [seatmapError, setSeatmapError] = useState(null);
	const [showPrintModal, setShowPrintModal] = useState(false);
	const [selectedTrains, setSelectedTrains] = useState([]);
	const printRef = useRef(null);

	const getSeatSortKey = (seatCode) => {
		if (!seatCode) return [Number.MAX_SAFE_INTEGER, ""];
		const code = String(seatCode);
		if (/^\d+$/.test(code)) {
			return [parseInt(code, 10), ""];
		}
		const match = code.match(/^(\d+)([A-Za-z]+)$/);
		if (match) {
			return [parseInt(match[1], 10), match[2]];
		}
		return [Number.MAX_SAFE_INTEGER, code];
	};

	const getSeatIndex = (userId, seatId) => {
		if (!userId || !seatId) return 0;
		const seats = userSeatNumbers[userId];
		if (!Array.isArray(seats)) return 0;
		return seats.indexOf(seatId) + 1;
	};

	const resolveTrainTypeCode = (trainCode) => {
		const code = String(trainCode || "").toUpperCase().trim();
		if (code.includes("KTX")) return "KTX";
		if (code.includes("ITX")) return "ITX-SAE";
		if (code.includes("SANTA")) return "SANTA";
		if (code.includes("국악와인") || code.includes("GUGAK") || code.includes("와인열차")) return "GUGAK_WINE";
		if (/^[0-9]+$/.test(code)) return "KTX";
		return null;
	};

	const TRAIN_TYPE_LABELS = {
		KTX: "KTX",
		"ITX-SAE": "ITX-새마을",
		SANTA: "산타열차",
		GUGAK_WINE: "국악와인열차",
	};

	const formatTrainTypeLabel = (trainTypeCode) => {
		if (!trainTypeCode) return "알 수 없음";
		const normalized = String(trainTypeCode || "").toUpperCase().trim();
		const label = TRAIN_TYPE_LABELS[normalized];
		if (!label) return normalized;
		return label === normalized ? label : `${label} (${normalized})`;
	};

	const resolvedTrainTypeCode = useMemo(() => {
		const firstCarData = Object.values(trainData || {})[0];
		const trainTypeFromData =
			firstCarData && firstCarData.train_type_code
				? String(firstCarData.train_type_code)
				: null;
		return trainTypeFromData || resolveTrainTypeCode(trainNumber);
	}, [trainData, trainNumber]);

	const resolvedTrainTypeLabel = useMemo(
		() => formatTrainTypeLabel(resolvedTrainTypeCode),
		[resolvedTrainTypeCode]
	);

	// 좌석 렌더링 함수 - 클릭 기능 제거
	// 좌석 렌더링 함수
	const renderSeat = (seatInfo) => {
		// 좌석 정보가 없으면 빈 좌석 표시
		if (!seatInfo) {
			return (
				<div className={SEAT_STYLES.empty.container}>
					<span className={SEAT_STYLES.empty.text}></span>
				</div>
			);
		}

		// 배정되지 않은 좌석
		if (!seatInfo.is_assigned) {
			return (
				<div className={SEAT_STYLES.available.container}>
					<span className={SEAT_STYLES.available.text}>-</span>
				</div>
			);
		}

		// 배정된 좌석
		const userId = seatInfo.user_id;
		const userName = seatInfo.res_nm || "";
		const phoneDisplay = seatInfo.phone_last4 ? `(${seatInfo.phone_last4})` : "";
		const userSeatNumber = getSeatIndex(userId, seatInfo.id);

		return (
			<div className={SEAT_STYLES.assigned.container}>
				<span className={SEAT_STYLES.assigned.nameText}>
					{userName}
				</span>
				{seatInfo.phone_last4 && (
					<span className="text-[9px] text-blue-500">
						{phoneDisplay}
					</span>
				)}
				<span className={SEAT_STYLES.assigned.countText}>
					{userSeatNumber}/{totalTeamCounts[userId] || 0}
				</span>
			</div>
		);
	};

	// 인쇄용 좌석 렌더링 함수
	const renderSeatForPrint = (seatInfo) => {
		// 좌석 정보가 없으면 빈 좌석 표시
		if (!seatInfo) {
			return `
				<div style="width: 30px; height: 30px; background-color: #f9fafb; border: 2px solid #e5e7eb; border-radius: 4px; display: flex; align-items: center; justify-content: center; margin: 2px;">
					<span style="color: #d1d5db; font-size: 12px;"></span>
				</div>
			`;
		}

		// 배정되지 않은 좌석 - 초록 좌석 표시(배정 가능)
		if (!seatInfo.is_assigned) {
			return `
				<div style="width: 30px; height: 30px; background-color: white; border: 2px solid #10b981; color: #059669; border-radius: 4px; display: flex; flex-direction: column; align-items: center; justify-content: center; margin: 2px;">
					<span style="font-size: 12px; font-weight: 500;">-</span>
				</div>
			`;
		}

		// 배정된 좌석 - 파랑 좌석 표시(배정 완료)
		const userId = seatInfo.user_id;
		const userName = seatInfo.res_nm || "";
		const phoneDisplay = seatInfo.phone_last4 ? `(${seatInfo.phone_last4})` : "";
		const userSeatNumber = getSeatIndex(userId, seatInfo.id);

		return `
			<div style="width: 30px; height: 30px; background-color: white; border: 2px solid #3b82f6; color: #1d4ed8; border-radius: 4px; display: flex; flex-direction: column; align-items: center; justify-content: center; margin: 2px;">
				<span style="font-size: 10px; font-weight: 500; text-align: center; overflow: hidden; text-overflow: ellipsis; width: 100%;">${userName}</span>
				<span style="font-size: 9px; color: #3b82f6;">${phoneDisplay}</span>
				<span style="font-size: 9px; color: #3b82f6;">${userSeatNumber}/${
			totalTeamCounts[userId] || 0
		}</span>
			</div>
		`;
	};

	const OBJECT_LABELS = {
		toilet: "WC",
		door: "DOOR",
		vestibule: "VEST",
		luggage: "LUGGAGE",
		table: "TABLE",
		wheelchair: "WHEEL",
		wheelchair_space: "WHEEL",
		bulkhead: "BULK",
		blocked: "BLOCK",
		kiosk: "KIOSK",
		aed: "AED",
		stage: "무대",
		wine_bar: "와인바",
		broadcast_room: "방송실",
		partition: "구분선",
	};

	const OBJECT_STYLES = {
		container:
			"w-16 h-12 bg-gray-100 border border-gray-300 rounded-md shadow-sm flex items-center justify-center text-[10px] text-gray-600",
		wheelchairForward:
			"w-16 h-12 bg-blue-50 border border-blue-300 rounded-md flex flex-col items-center justify-center text-[10px] text-blue-700",
		wheelchairBackward:
			"w-16 h-12 bg-rose-50 border border-rose-300 rounded-md flex flex-col items-center justify-center text-[10px] text-rose-700",
		stage:
			"w-16 h-12 bg-purple-100 border border-purple-400 rounded-md flex items-center justify-center text-[10px] text-purple-700 font-bold",
		wine_bar:
			"w-16 h-12 bg-red-100 border border-red-400 rounded-md flex items-center justify-center text-[10px] text-red-700 font-bold",
		broadcast_room:
			"w-16 h-12 bg-green-100 border border-green-400 rounded-md flex items-center justify-center text-[10px] text-green-700 font-bold",
	};

	const getObjectLabel = (object) => {
		if (!object) return "";
		const rawType = String(object.object_type || "");
		const base = OBJECT_LABELS[rawType] || rawType.toUpperCase();
		return object.object_id ? `${base}:${object.object_id}` : base;
	};

	const resolveLayoutColumns = (layoutConfig) => {
		if (Array.isArray(layoutConfig?.columns) && layoutConfig.columns.length > 0) {
			return layoutConfig.columns;
		}
		if (
			Array.isArray(layoutConfig?.seat_columns) &&
			layoutConfig.seat_columns.length > 0
		) {
			return layoutConfig.seat_columns;
		}
		return null;
	};

	const resolveNumbering = (layoutConfig, seatInfoByCode) => {
		const raw = String(layoutConfig?.numbering || "").toLowerCase();
		if (raw === "numeric" || raw === "alpha") {
			return raw;
		}
		const firstSeatCode = Object.keys(seatInfoByCode || {})[0];
		if (firstSeatCode && !isNaN(firstSeatCode)) {
			return "numeric";
		}
		return "alpha";
	};

	const resolveAisleAfter = (layoutConfig, columns) => {
		const raw =
			Number.isInteger(layoutConfig?.aisle_after)
				? layoutConfig.aisle_after
				: Number.isInteger(layoutConfig?.aisleAfter)
					? layoutConfig.aisleAfter
					: null;
		if (
			Number.isInteger(raw) &&
			raw > 0 &&
			Array.isArray(columns) &&
			raw < columns.length
		) {
			return raw;
		}
		return Math.floor((columns || []).length / 2);
	};

	const parseSeatCode = (seatCode, numbering, columns) => {
		if (!seatCode) return null;
		if (numbering === "numeric") {
			const seatNum = parseInt(seatCode, 10);
			if (Number.isNaN(seatNum)) return null;
			const cols =
				Array.isArray(columns) && columns.length > 0
					? columns
					: ["1", "2", "3", "4"];
			const colIndex = (seatNum - 1) % cols.length;
			return {
				row: Math.floor((seatNum - 1) / cols.length) + 1,
				col: String(cols[colIndex]),
			};
		}

		const match = String(seatCode).match(/^(\d+)([A-Za-z]+)$/);
		if (!match) return null;
		return {
			row: parseInt(match[1], 10),
			col: match[2],
		};
	};

	const resolveColumns = (layoutColumns, seatCols, numbering) => {
		if (Array.isArray(layoutColumns) && layoutColumns.length > 0) {
			return layoutColumns;
		}
		const derived = Array.from(seatCols || []);
		if (derived.length > 0) {
			if (numbering === "numeric") {
				derived.sort((a, b) => {
					const aNum = parseInt(a, 10);
					const bNum = parseInt(b, 10);
					if (Number.isNaN(aNum) || Number.isNaN(bNum)) {
						return String(a).localeCompare(String(b));
					}
					return aNum - bNum;
				});
			} else {
				derived.sort((a, b) => String(a).localeCompare(String(b)));
			}
			return derived;
		}
		return numbering === "numeric"
			? ["1", "2", "3", "4"]
			: ["A", "B", "C", "D"];
	};

	const resolveRows = (layoutConfig, seatRows, fallbackRows) => {
		const start = parseInt(layoutConfig?.row_start, 10);
		const end = parseInt(layoutConfig?.row_end, 10);
		if (
			Number.isInteger(start) &&
			Number.isInteger(end) &&
			start <= end
		) {
			return Array.from({ length: end - start + 1 }, (_, idx) => start + idx);
		}

		const derived = Array.from(seatRows || [])
			.map((row) => parseInt(row, 10))
			.filter((row) => Number.isInteger(row))
			.sort((a, b) => a - b);
		if (derived.length > 0) {
			return derived;
		}

		if (Array.isArray(fallbackRows) && fallbackRows.length > 0) {
			return fallbackRows
				.map((row) => parseInt(row, 10))
				.filter((row) => Number.isInteger(row))
				.sort((a, b) => a - b);
		}

		return [];
	};

	const resolveLayoutCar = (layoutCars, trainId) => {
		const target = parseInt(trainId, 10);
		return (layoutCars || []).find((car) => {
			const carNo = parseInt(car?.car_number, 10);
			if (Number.isNaN(target) || Number.isNaN(carNo)) {
				return String(car?.car_number) === String(trainId);
			}
			return carNo === target;
		});
	};

	const getDirectionKey = (direction) => {
		const normalized = String(direction || "").toLowerCase();
		if (normalized === "backward") return "backward";
		if (normalized === "forward") return "forward";
		if (normalized === "left") return "left";
		if (normalized === "right") return "right";
		return "unknown";
	};

	const directionPalette = {
		forward: {
			assigned: "bg-emerald-500 border-emerald-600 text-white",
			available: "border-emerald-500 text-emerald-600",
		},
		backward: {
			assigned: "bg-rose-400 border-rose-500 text-white",
			available: "border-rose-400 text-rose-500",
		},
		left: {
			assigned: "bg-amber-500 border-amber-600 text-white",
			available: "border-amber-500 text-amber-600",
		},
		right: {
			assigned: "bg-sky-500 border-sky-600 text-white",
			available: "border-sky-500 text-sky-600",
		},
		unknown: {
			assigned: "bg-slate-400 border-slate-500 text-white",
			available: "border-slate-400 text-slate-500",
		},
	};

	// 방향 화살표 배지 (↑↓←→)
	const DirectionBadge = ({ direction }) => {
		const normalized = String(direction || "").toLowerCase();
		const arrows = {
			forward: "↑",
			backward: "↓",
			left: "←",
			right: "→",
		};
		const arrow = arrows[normalized];
		if (!arrow) return null;
		return (
			<span className="absolute -top-1 -right-1 text-[10px] font-bold opacity-60">
				{arrow}
			</span>
		);
	};

	// 좌석 등받이 표시 (방향에 따라 위치 변경)
	const SeatBackrest = ({ direction }) => {
		const normalized = String(direction || "").toLowerCase();
		const config = {
			forward: { position: "-left-2 top-2", size: "w-2 h-7", color: "bg-emerald-600/40" },
			backward: { position: "-right-2 top-2", size: "w-2 h-7", color: "bg-rose-500/40" },
			left: { position: "left-2 -top-2", size: "h-2 w-7", color: "bg-amber-500/40" },
			right: { position: "left-2 -bottom-2", size: "h-2 w-7", color: "bg-sky-500/40" },
		};
		const c = config[normalized] || { position: "-left-2 top-2", size: "w-2 h-7", color: "bg-slate-400/30" };
		return (
			<div className={`absolute ${c.position} ${c.size} rounded-sm ${c.color}`} />
		);
	};

	const EmptyCell = ({ label }) => (
		<div className="w-16 h-12 bg-gray-100 border border-gray-300 rounded-md shadow-sm flex items-center justify-center text-gray-400 relative">
			{label ? (
				<span className="text-[11px] text-gray-400">{label}</span>
			) : (
				<span className="text-gray-300"></span>
			)}
		</div>
	);

	const BlankCell = () => <div className="w-16 h-12"></div>;

	const TableCell = ({ object }) => (
		<div
			className="w-16 h-12 flex items-center justify-center"
			title={object?.notes || object?.position_hint || ""}
		>
			<div className="flex flex-col items-center gap-0.5">
				<div className="w-8 h-2 bg-amber-200 border border-amber-400 rounded-full"></div>
				<div className="w-6 h-1 bg-amber-300 rounded-full"></div>
			</div>
		</div>
	);

	const WheelchairCell = ({ object }) => {
		const dir = String(object?.direction || "").toLowerCase();
		const styleClass =
			dir === "backward"
				? OBJECT_STYLES.wheelchairBackward
				: OBJECT_STYLES.wheelchairForward;
		return (
			<div
				className={`${styleClass} relative`}
				title={object?.notes || object?.position_hint || ""}
			>
				<span className="text-base" role="img" aria-label="wheelchair">
					♿
				</span>
			</div>
		);
	};

	const BlockedCell = ({ object }) => (
		<div
			className="w-16 h-12 bg-gray-200 border border-gray-300 rounded-md shadow-sm relative overflow-hidden"
			title={object?.notes || object?.position_hint || object?.object_id || ""}
		>
			<div className="absolute inset-0 bg-[linear-gradient(135deg,transparent_45%,rgba(107,114,128,0.5)_45%,rgba(107,114,128,0.5)_55%,transparent_55%)]"></div>
		</div>
	);

	const ObjectCell = ({ object }) => {
		if (!object) return null;
		if (object.object_type === "table") {
			return <TableCell object={object} />;
		}
		if (object.object_type === "wheelchair_space") {
			return <WheelchairCell object={object} />;
		}
		if (object.object_type === "blocked") {
			return <BlockedCell object={object} />;
		}
		// 특수 시설 (무대, 와인바, 방송실)
		if (["stage", "wine_bar", "broadcast_room"].includes(object.object_type)) {
			const styleClass = OBJECT_STYLES[object.object_type] || OBJECT_STYLES.container;
			return (
				<div
					className={styleClass}
					title={object?.notes || object?.position_hint || ""}
				>
					<span>{getObjectLabel(object)}</span>
				</div>
			);
		}
		return (
			<div
				className={OBJECT_STYLES.container}
				title={object?.notes || object?.position_hint || ""}
			>
				<span>{getObjectLabel(object)}</span>
			</div>
		);
	};

	const SeatCell = ({ seatCode, seatInfo, seatDirection }) => {
		if (!seatInfo) {
			return <EmptyCell label={seatCode} />;
		}

		if (!seatInfo.is_assigned) {
			const palette = directionPalette[getDirectionKey(seatDirection)];
			return (
				<div
					className={`w-16 h-12 bg-white border-2 rounded-md shadow-sm flex flex-col items-center justify-center ${palette.available} relative`}
				>
					<SeatBackrest direction={seatDirection} />
					<span className="text-[10px] font-semibold">{seatCode}</span>
				</div>
			);
		}

		const userId = seatInfo.user_id;
		const userName = seatInfo.res_nm || "";
		const phoneDisplay = seatInfo.phone_last4 ? `(${seatInfo.phone_last4})` : "";
		const userSeatNumber = getSeatIndex(userId, seatInfo.id);
		const palette = directionPalette[getDirectionKey(seatDirection)];

		return (
			<div
				className={`w-16 h-12 rounded-md shadow-sm flex flex-col items-center justify-center border ${palette.assigned} relative`}
			>
				<SeatBackrest direction={seatDirection} />
				<span className="text-[10px] font-medium w-full text-center truncate px-0.5 leading-tight">
					{userName}
				</span>
				{seatInfo.phone_last4 && (
					<span className="text-[9px] text-white/80 leading-tight">
						{phoneDisplay}
					</span>
				)}
				<span className="text-[9px] text-white/80 leading-tight">
					{userSeatNumber}/{totalTeamCounts[userId] || 0}
				</span>
			</div>
		);
	};

	/**
	 * 행 사이 테이블 바 컴포넌트 (마주보는 좌석 사이)
	 */
	const InlineTableBar = ({ tableSlot, columns, aisleAfter }) => {
		if (!tableSlot) return null;
		const { left, right } = tableSlot;

		const safeColumns =
			Array.isArray(columns) && columns.length > 0
				? columns
				: ["A", "B", "C", "D"];
		const aisleIndex =
			Number.isInteger(aisleAfter) &&
			aisleAfter > 0 &&
			aisleAfter < safeColumns.length
				? aisleAfter
				: Math.floor(safeColumns.length / 2);

		const leftColCount = aisleIndex;
		const rightColCount = safeColumns.length - aisleIndex;

		// 테이블 바 너비 계산 (좌석 너비 64px + gap 8px)
		const leftBarWidth = leftColCount * 64 + (leftColCount - 1) * 8;
		const rightBarWidth = rightColCount * 64 + (rightColCount - 1) * 8;

		return (
			<div className="flex items-center my-1">
				{/* 행 번호 영역 (빈 공간) */}
				<div className="w-8"></div>

				{/* 테이블 바 영역 */}
				<div className="flex gap-2">
					{/* 왼쪽 테이블 바 */}
					<div className="flex gap-2">
						{left ? (
							<div
								className="h-3 bg-gradient-to-r from-amber-200 via-amber-300 to-amber-200 border border-amber-400 rounded-full shadow-sm flex items-center justify-center"
								style={{ width: `${leftBarWidth}px` }}
								title={left.notes || "테이블"}
							>
								<div className="w-1/2 h-1 bg-amber-400/50 rounded-full"></div>
							</div>
						) : (
							<div style={{ width: `${leftBarWidth}px` }} className="h-3"></div>
						)}
					</div>

					{/* 통로 */}
					<div className="w-8"></div>

					{/* 오른쪽 테이블 바 */}
					<div className="flex gap-2">
						{right ? (
							<div
								className="h-3 bg-gradient-to-r from-amber-200 via-amber-300 to-amber-200 border border-amber-400 rounded-full shadow-sm flex items-center justify-center"
								style={{ width: `${rightBarWidth}px` }}
								title={right.notes || "테이블"}
							>
								<div className="w-1/2 h-1 bg-amber-400/50 rounded-full"></div>
							</div>
						) : (
							<div style={{ width: `${rightBarWidth}px` }} className="h-3"></div>
						)}
					</div>
				</div>
			</div>
		);
	};

	/**
	 * 행 사이 구분선/칸막이 컴포넌트
	 */
	const PartitionLine = ({ partition, columns, aisleAfter }) => {
		if (!partition) return null;

		const safeColumns =
			Array.isArray(columns) && columns.length > 0
				? columns
				: ["A", "B", "C", "D"];
		const aisleIndex =
			Number.isInteger(aisleAfter) &&
			aisleAfter > 0 &&
			aisleAfter < safeColumns.length
				? aisleAfter
				: Math.floor(safeColumns.length / 2);

		const leftColCount = aisleIndex;
		const rightColCount = safeColumns.length - aisleIndex;

		// 전체 너비 계산 (좌석 너비 64px + gap 8px + 통로 32px)
		const totalWidth = (leftColCount + rightColCount) * 64 + (safeColumns.length - 1) * 8 + 32;

		return (
			<div className="flex items-center my-2">
				{/* 행 번호 영역 (빈 공간) */}
				<div className="w-8"></div>

				{/* 구분선 영역 */}
				<div
					className="h-1.5 bg-gradient-to-r from-gray-300 via-gray-400 to-gray-300 rounded-full shadow-sm relative"
					style={{ width: `${totalWidth}px` }}
					title={partition.notes || "구분선"}
				>
					{/* 중앙 장식 */}
					<div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-gray-500 rounded-full border-2 border-white shadow"></div>
				</div>
			</div>
		);
	};

	const SeatRow = ({
		row,
		columns,
		aisleAfter,
		seatCodeByRowCol,
		seatDirectionByCode,
		objectByRowCol,
		seatInfoByCode,
	}) => {
		const safeColumns =
			Array.isArray(columns) && columns.length > 0
				? columns
				: ["A", "B", "C", "D"];
		const aisleIndex =
			Number.isInteger(aisleAfter) &&
			aisleAfter > 0 &&
			aisleAfter < safeColumns.length
				? aisleAfter
				: Math.floor(safeColumns.length / 2);

		const leftColumns = safeColumns.slice(0, aisleIndex);
		const rightColumns = safeColumns.slice(aisleIndex);

		const renderCell = (col) => {
			const key = `${row}-${col}`;
			const seatCode = seatCodeByRowCol?.get
				? seatCodeByRowCol.get(key)
				: null;
			if (seatCode) {
				const seatInfo = seatInfoByCode ? seatInfoByCode[seatCode] : null;
				const seatDirection = seatDirectionByCode?.get
					? seatDirectionByCode.get(seatCode)
					: null;
				return (
					<SeatCell
						key={`${key}-seat`}
						seatCode={seatCode}
						seatInfo={seatInfo}
						seatDirection={seatDirection}
					/>
				);
			}

			const object = objectByRowCol?.get ? objectByRowCol.get(key) : null;
			if (object) {
				if (object.object_type === "blocked") {
					return <BlankCell key={`${key}-blocked`} />;
				}
				return <ObjectCell key={`${key}-object`} object={object} />;
			}

			return <EmptyCell key={`${key}-empty`} />;
		};

		return (
			<div className="flex items-center mb-2">
				<div className="w-8 text-center font-bold">{row}</div>
				<div className="flex gap-2">
					<div className="flex gap-2">
						{leftColumns.map((col) => renderCell(col))}
					</div>
					<div className="w-8"></div>
					<div className="flex gap-2">
						{rightColumns.map((col) => renderCell(col))}
					</div>
				</div>
			</div>
		);
	};

	const SeatGridHorizontal = ({
		rowNumbers,
		columns,
		aisleAfter,
		seatCodeByRowCol,
		seatDirectionByCode,
		objectByRowCol,
		seatInfoByCode,
		tableSlotsByAfterRow,
	}) => {
		const safeRows = Array.isArray(rowNumbers) ? rowNumbers : [];
		const safeColumns =
			Array.isArray(columns) && columns.length > 0
				? columns
				: ["A", "B", "C", "D"];
		const aisleIndex =
			Number.isInteger(aisleAfter) &&
			aisleAfter > 0 &&
			aisleAfter < safeColumns.length
				? aisleAfter
				: Math.floor(safeColumns.length / 2);
		const topColumns = safeColumns.slice(0, aisleIndex);
		const bottomColumns = safeColumns.slice(aisleIndex);
		const rowSlots = [];
		safeRows.forEach((rowNum) => {
			rowSlots.push({ type: "row", row: rowNum });
			if (tableSlotsByAfterRow?.has(rowNum)) {
				rowSlots.push({ type: "table", afterRow: rowNum });
			}
		});

		return (
			<div className="flex flex-col gap-2">
				{rowSlots.length > 0 && (
					<div className="flex items-center mb-2">
						<div className="w-8"></div>
						<div className="flex gap-2">
							{rowSlots.map((slot, idx) =>
								slot.type === "row" ? (
									<div
										key={`row-header-${slot.row}`}
										className="w-16 text-center font-bold"
									>
										{slot.row}
									</div>
								) : (
									<div key={`row-header-table-${idx}`} className="w-16"></div>
								)
							)}
						</div>
					</div>
				)}

				{topColumns.map((col, idx) => (
					<SeatRowHorizontal
						key={`seat-row-top-${col}`}
						col={col}
						rowSlots={rowSlots}
						seatCodeByRowCol={seatCodeByRowCol}
						seatDirectionByCode={seatDirectionByCode}
						objectByRowCol={objectByRowCol}
						seatInfoByCode={seatInfoByCode}
						tableSlotsByAfterRow={tableSlotsByAfterRow}
						side="left"
						isPrimaryRow={idx === 0}
					/>
				))}

				{bottomColumns.length > 0 && rowSlots.length > 0 && (
					<div className="flex items-center mb-2">
						<div className="w-8"></div>
						<div className="flex gap-2">
							{rowSlots.map((slot, idx) => (
								<div key={`aisle-${idx}-${slot.type}`} className="w-16 h-4"></div>
							))}
						</div>
					</div>
				)}

				{bottomColumns.map((col, idx) => (
					<SeatRowHorizontal
						key={`seat-row-bottom-${col}`}
						col={col}
						rowSlots={rowSlots}
						seatCodeByRowCol={seatCodeByRowCol}
						seatDirectionByCode={seatDirectionByCode}
						objectByRowCol={objectByRowCol}
						seatInfoByCode={seatInfoByCode}
						tableSlotsByAfterRow={tableSlotsByAfterRow}
						side="right"
						isPrimaryRow={idx === 0}
					/>
				))}
			</div>
		);
	};

	const SeatRowHorizontal = ({
		col,
		rowSlots,
		seatCodeByRowCol,
		seatDirectionByCode,
		objectByRowCol,
		seatInfoByCode,
		tableSlotsByAfterRow,
		side,
		isPrimaryRow,
	}) => {
		const safeSlots = Array.isArray(rowSlots) ? rowSlots : [];

		const renderSlot = (slot) => {
			if (slot.type === "table") {
				const tableSlot = tableSlotsByAfterRow?.get
					? tableSlotsByAfterRow.get(slot.afterRow)
					: null;
				const tableObject = tableSlot ? tableSlot[side] : null;
				if (tableObject && isPrimaryRow) {
					return (
						<ObjectCell
							key={`table-${side}-${slot.afterRow}`}
							object={tableObject}
						/>
					);
				}
				return <div key={`table-spacer-${side}-${slot.afterRow}`} className="w-16 h-12"></div>;
			}

			const key = `${slot.row}-${col}`;
			const seatCode = seatCodeByRowCol?.get
				? seatCodeByRowCol.get(key)
				: null;
			if (seatCode) {
				const seatInfo = seatInfoByCode ? seatInfoByCode[seatCode] : null;
				const seatDirection = seatDirectionByCode?.get
					? seatDirectionByCode.get(seatCode)
					: null;
				return (
					<SeatCell
						key={`${key}-seat`}
						seatCode={seatCode}
						seatInfo={seatInfo}
						seatDirection={seatDirection}
					/>
				);
			}

			const object = objectByRowCol?.get ? objectByRowCol.get(key) : null;
			if (object) {
				if (object.object_type === "blocked") {
					return <BlankCell key={`${key}-blocked`} />;
				}
				return <ObjectCell key={`${key}-object`} object={object} />;
			}

			return <EmptyCell key={`${key}-empty`} />;
		};

		return (
			<div className="flex items-center mb-2">
				<div className="w-8 text-center font-bold">{col}</div>
				<div className="flex gap-2">
					{safeSlots.map((slot) => renderSlot(slot))}
				</div>
			</div>
		);
	};

	const renderLegacyCar = (trainId) => {
		const carData = trainData[trainId];
		if (!carData) return null;

		return (
			<div key={`train-${trainId}`} className="border p-4 rounded-lg">
				<h4 className="text-lg font-bold mb-4">{trainId}호차</h4>
				<div className="flex justify-center">
					<div className="flex flex-col gap-4">
						<div className="flex gap-2 items-center ml-8">
							{isNumericSystem ? (
								<>
									<div className="w-16 text-center font-bold ml-2">1</div>
									<div className="w-16 text-center font-bold">2</div>
									<div className="w-8"></div>
									<div className="w-16 text-center font-bold">3</div>
									<div className="w-16 text-center font-bold">4</div>
								</>
							) : (
								<>
									<div className="w-16 text-center font-bold ml-2">A</div>
									<div className="w-16 text-center font-bold">B</div>
									<div className="w-8"></div>
									<div className="w-16 text-center font-bold">C</div>
									<div className="w-16 text-center font-bold">D</div>
								</>
							)}
						</div>

						{carData.rows.map((rowNum) => (
							<div
								key={`row-${trainId}-${rowNum}`}
								className="flex gap-2 items-center"
							>
								<div className="w-8 text-center font-bold">{rowNum}</div>

								{isNumericSystem ? (
									<>
										{renderSeat(
											carData.data[`${rowNum * 4 + 1}`]
										)}
										{renderSeat(
											carData.data[`${rowNum * 4 + 2}`]
										)}
										<div className="w-8"></div>
										{renderSeat(
											carData.data[`${rowNum * 4 + 3}`]
										)}
										{renderSeat(
											carData.data[`${rowNum * 4 + 4}`]
										)}
									</>
								) : (
									<>
										{renderSeat(carData.data[`${rowNum}A`])}
										{renderSeat(carData.data[`${rowNum}B`])}
										<div className="w-8"></div>
										{renderSeat(carData.data[`${rowNum}C`])}
										{renderSeat(carData.data[`${rowNum}D`])}
									</>
								)}
							</div>
						))}
					</div>
				</div>
			</div>
		);
	};

	const renderSeatmapCar = (trainId) => {
		const carData = trainData[trainId];
		if (!carData) return null;

		const layoutCars = seatmapLayout?.cars || [];
		const layoutCar = resolveLayoutCar(layoutCars, trainId);
		if (!seatmapLayout || !layoutCar) {
			return renderLegacyCar(trainId);
		}

		const seatInfoByCode = carData.data || {};
		const layoutConfig = layoutCar.layout || {};
		const carObjects = Array.isArray(layoutCar.objects) ? layoutCar.objects : [];
		const seatObjects = carObjects.filter(
			(obj) => obj.object_type === "seat"
		);
		const nonSeatObjects = carObjects.filter(
			(obj) => obj.object_type !== "seat"
		);
		const layoutColumns = resolveLayoutColumns(layoutConfig);
		const numbering = resolveNumbering(layoutConfig, seatInfoByCode);

		const seatCodeByRowCol = new Map();
		const seatDirectionByCode = new Map();
		const seatRows = new Set();
		const seatCols = new Set();
		const seatDirectionByRow = new Map();

		const registerSeat = (rowValue, colValue, seatCode, direction) => {
			if (!rowValue || !colValue || !seatCode) return;
			const rowNum = parseInt(rowValue, 10);
			const colStr = String(colValue);
			if (!Number.isInteger(rowNum)) return;
			seatCodeByRowCol.set(`${rowNum}-${colStr}`, seatCode);
			seatRows.add(rowNum);
			seatCols.add(colStr);
			if (direction) {
				seatDirectionByCode.set(seatCode, direction);
				if (!seatDirectionByRow.has(rowNum)) {
					seatDirectionByRow.set(rowNum, direction);
				}
			}
		};

		if (seatObjects.length > 0) {
			seatObjects.forEach((obj) => {
				let rowValue = obj.row;
				let colValue = obj.col;
				let seatCode = obj.seat_code;

				if ((!rowValue || !colValue) && seatCode) {
					const parsed = parseSeatCode(seatCode, numbering, layoutColumns);
					if (parsed) {
						rowValue = rowValue || parsed.row;
						colValue = colValue || parsed.col;
					}
				}

				if (!seatCode && numbering === "alpha" && rowValue && colValue) {
					seatCode = `${rowValue}${colValue}`;
				}

				if (seatCode) {
					registerSeat(rowValue, colValue, seatCode, obj.direction);
				}
			});
		} else {
			Object.keys(seatInfoByCode).forEach((seatCode) => {
				const parsed = parseSeatCode(seatCode, numbering, layoutColumns);
				if (parsed) {
					registerSeat(parsed.row, parsed.col, seatCode, null);
				}
			});
		}

		const columns = resolveColumns(layoutColumns, seatCols, numbering);
		const aisleAfter = resolveAisleAfter(layoutConfig, columns);

		const objectByRowCol = new Map();
		const outsideObjects = [];
		const tableSlotsByAfterRow = new Map();
		const partitionByAfterRow = new Map();

		// 테이블용 between_rows 파싱 (left/right side 포함)
		const parseBetweenRowsHint = (hint) => {
			if (!hint) return null;
			const match = hint.match(/between_rows_(\d+)_(\d+)_(left|right)/i);
			if (!match) return null;
			return {
				afterRow: parseInt(match[1], 10),
				beforeRow: parseInt(match[2], 10),
				side: match[3].toLowerCase(),
			};
		};

		// 파티션용 between_rows 파싱 (side 없음)
		const parsePartitionHint = (hint) => {
			if (!hint) return null;
			const match = hint.match(/between_rows_(\d+)_(\d+)$/i);
			if (!match) return null;
			return {
				afterRow: parseInt(match[1], 10),
				beforeRow: parseInt(match[2], 10),
			};
		};

		const resolveWheelchairDirection = (obj) => {
			if (!obj || obj.object_type !== "wheelchair_space" || obj.direction) {
				return obj?.direction || null;
			}
			let row = parseInt(obj.row, 10);
			if (!Number.isInteger(row) && obj.position_hint) {
				const match = obj.position_hint.match(/rows?_([0-9]+)_([0-9]+)/i);
				if (match) {
					row = parseInt(match[1], 10);
				}
			}
			if (Number.isInteger(row)) {
				return seatDirectionByRow.get(row) || null;
			}
			return null;
		};

		nonSeatObjects.forEach((obj) => {
			const derivedDirection = resolveWheelchairDirection(obj);
			const normalizedObj =
				derivedDirection && !obj.direction
					? { ...obj, direction: derivedDirection }
					: obj;

			// 테이블 처리
			const betweenRows =
				normalizedObj.object_type === "table"
					? parseBetweenRowsHint(normalizedObj.position_hint)
					: null;
			if (betweenRows) {
				const slot = tableSlotsByAfterRow.get(betweenRows.afterRow) || {
					left: null,
					right: null,
				};
				slot[betweenRows.side] = normalizedObj;
				tableSlotsByAfterRow.set(betweenRows.afterRow, slot);
				return;
			}

			// 파티션 처리
			const partitionHint =
				normalizedObj.object_type === "partition"
					? parsePartitionHint(normalizedObj.position_hint)
					: null;
			if (partitionHint) {
				partitionByAfterRow.set(partitionHint.afterRow, normalizedObj);
				return;
			}

			const row = parseInt(normalizedObj.row, 10);
			const col =
				normalizedObj.col === null || normalizedObj.col === undefined
					? null
					: String(normalizedObj.col);
			if (Number.isInteger(row) && col) {
				objectByRowCol.set(`${row}-${col}`, normalizedObj);
			} else if (normalizedObj.position_hint) {
				outsideObjects.push(normalizedObj);
			}
		});

		const rowNumbers = resolveRows(layoutConfig, seatRows, carData.rows);
		const orientation = String(layoutConfig.orientation || "horizontal").toLowerCase();
		const isHorizontal = orientation === "horizontal";

		return (
			<div key={`train-${trainId}`} className="border p-4 rounded-lg">
				<h4 className="text-lg font-bold mb-4">
					{layoutCar?.label
						? `${trainId}호차 · ${layoutCar.label}`
						: `${trainId}호차`}
				</h4>
				{outsideObjects.length > 0 && (
					<div className="mb-4 flex flex-wrap gap-2 text-xs text-gray-600">
						{outsideObjects.map((obj, idx) => (
							<span
								key={`${trainId}-outside-${idx}`}
								className="px-2 py-1 bg-gray-100 rounded"
								title={obj.notes || obj.position_hint || ""}
							>
								{getObjectLabel(obj)}
							</span>
						))}
					</div>
				)}
				<div className="overflow-x-auto">
					<div className="min-w-max mx-auto">
						{isHorizontal ? (
							<SeatGridHorizontal
								rowNumbers={rowNumbers}
								columns={columns}
								aisleAfter={aisleAfter}
								seatCodeByRowCol={seatCodeByRowCol}
								seatDirectionByCode={seatDirectionByCode}
								objectByRowCol={objectByRowCol}
								seatInfoByCode={seatInfoByCode}
								tableSlotsByAfterRow={tableSlotsByAfterRow}
							/>
						) : (
							<div className="flex flex-col gap-4">
								{columns.length > 0 && (
									<div className="flex gap-2 items-center ml-8">
										<div className="flex gap-2">
											{columns.slice(0, aisleAfter).map((col, idx) => (
												<div
													key={`${trainId}-col-left-${col}`}
													className={`w-16 text-center font-bold ${
														idx === 0 ? "ml-2" : ""
													}`}
												>
													{col}
												</div>
											))}
										</div>
										<div className="w-8"></div>
										<div className="flex gap-2">
											{columns.slice(aisleAfter).map((col) => (
												<div
													key={`${trainId}-col-right-${col}`}
													className="w-16 text-center font-bold"
												>
													{col}
												</div>
											))}
										</div>
									</div>
								)}
								{rowNumbers.map((rowNum) => (
									<React.Fragment key={`${trainId}-row-group-${rowNum}`}>
										<SeatRow
											key={`${trainId}-${rowNum}`}
											row={rowNum}
											columns={columns}
											aisleAfter={aisleAfter}
											seatCodeByRowCol={seatCodeByRowCol}
											seatDirectionByCode={seatDirectionByCode}
											objectByRowCol={objectByRowCol}
											seatInfoByCode={seatInfoByCode}
										/>
										{/* 행 사이 테이블 바 */}
										{tableSlotsByAfterRow.has(rowNum) && (
											<InlineTableBar
												tableSlot={tableSlotsByAfterRow.get(rowNum)}
												columns={columns}
												aisleAfter={aisleAfter}
											/>
										)}
										{/* 행 사이 구분선/칸막이 */}
										{partitionByAfterRow.has(rowNum) && (
											<PartitionLine
												partition={partitionByAfterRow.get(rowNum)}
												columns={columns}
												aisleAfter={aisleAfter}
											/>
										)}
									</React.Fragment>
								))}
							</div>
						)}
					</div>
				</div>
			</div>
		);
	};

	// 모달이 열릴 때 티켓 상세 정보와 좌석 배치 정보를 가져옴
	useEffect(() => {
		if (isOpen && ticketItemIdsStr) {
			// totalTeamCounts 초기화
			setTotalTeamCounts({});
			setUserSeatNumbers({});

			// API 호출
			getSeatAssignments(
				ticketItemIdsStr,
				trainNumber,
				departureDate,
				departureStation,
				arrivalStation
			).then((response) => {
				if (response && response.success) {
					// API 응답에서 직접 원본 데이터 가져오기
					const apiResponse = response.originalData;
					if (apiResponse) {
						setTrainData(apiResponse);

						// 팀별 좌석 사용량 계산 - 새 객체 생성하여 불변성 유지
						const newTotalTeamCounts = {};
						const newUserSeatNumbers = {};

						Object.keys(apiResponse).forEach((trainId) => {
							const seatData = apiResponse[trainId].data || {};
							const seatCodes = Object.keys(seatData).sort((a, b) => {
								const [aRow, aCol] = getSeatSortKey(a);
								const [bRow, bCol] = getSeatSortKey(b);
								if (aRow !== bRow) return aRow - bRow;
								return String(aCol).localeCompare(String(bCol));
							});

							seatCodes.forEach((seatCode) => {
								const seatInfo = seatData[seatCode];
								if (seatInfo && seatInfo.is_assigned && seatInfo.user_id) {
									const userId = seatInfo.user_id;
									if (!newTotalTeamCounts[userId]) {
										newTotalTeamCounts[userId] = 0;
									}
									newTotalTeamCounts[userId]++;

									if (!newUserSeatNumbers[userId]) {
										newUserSeatNumbers[userId] = [];
									}
									if (seatInfo.id) {
										newUserSeatNumbers[userId].push(seatInfo.id);
									}
								}
							});
						});

						// 한 번에 상태 업데이트
						setTotalTeamCounts(newTotalTeamCounts);
						setUserSeatNumbers(newUserSeatNumbers);
					}
				}
			});
		}

		// 모달이 닫힐 때 상태 초기화
		return () => {
			if (!isOpen) {
				resetTicketDetails();
				setTrainData({});
				setSeatmapLayout(null);
				setSeatmapError(null);
				setSeatmapLoading(false);
			}
		};
	}, [isOpen, ticketItemIdsStr, getSeatAssignments, resetTicketDetails]);

	useEffect(() => {
		if (!isOpen) return;
		if (!resolvedTrainTypeCode) {
			setSeatmapLayout(null);
			setSeatmapError(null);
			return;
		}

		setSeatmapLoading(true);
		setSeatmapError(null);

		getSeatmapLayout(resolvedTrainTypeCode)
			.then((response) => {
				if (response?.success && response.layout) {
					setSeatmapLayout(response.layout);
				} else {
					setSeatmapLayout(null);
					setSeatmapError(
						response?.error || "좌석 배치도 레이아웃이 없습니다."
					);
				}
			})
			.catch((err) => {
				setSeatmapLayout(null);
				setSeatmapError(
					err?.message || "좌석 배치도 레이아웃을 불러오지 못했습니다."
				);
			})
			.finally(() => {
				setSeatmapLoading(false);
			});
	}, [isOpen, resolvedTrainTypeCode, getSeatmapLayout]);

	// trainData 구조 확인 및 좌석 시스템 결정 로직 추가
	useEffect(() => {
		if (Object.keys(trainData).length > 0) {
			const firstTrainId = Object.keys(trainData)[0];
			const firstRowNum = trainData[firstTrainId].rows[0];
			// 첫 번째 좌석 키를 확인하여 숫자 시스템인지 문자 시스템인지 판단
			const firstSeatKey = Object.keys(trainData[firstTrainId].data)[0];
			setIsNumericSystem(firstSeatKey && !isNaN(firstSeatKey));
		}
	}, [trainData]);

	// 호차 선택 모달 열기
	const handleOpenPrintModal = () => {
		setShowPrintModal(true);
		setSelectedTrains([]); // 선택 초기화
	};

	// 호차 선택 모달 닫기
	const handleClosePrintModal = () => {
		setShowPrintModal(false);
		setSelectedTrains([]);
	};

	// 호차 선택 처리
	const handleTrainSelection = (trainId) => {
		setSelectedTrains((prev) => {
			if (prev.includes(trainId)) {
				return prev.filter((id) => id !== trainId);
			} else {
				return [...prev, trainId];
			}
		});
	};

	// 모든 호차 선택/해제
	const handleSelectAll = () => {
		const allTrainIds = Object.keys(trainData);
		if (selectedTrains.length === allTrainIds.length) {
			setSelectedTrains([]);
		} else {
			setSelectedTrains(allTrainIds);
		}
	};

	// 인쇄 핸들러 - 브라우저 기본 인쇄 기능 사용
	const handlePrint = () => {
		if (selectedTrains.length === 0) {
			alert("인쇄할 호차를 선택해주세요.");
			return;
		}

		const printContent = document.createElement("div");

		// 선택된 호차들의 좌석 배치도 생성
		const trainCarHtmls = selectedTrains.map((trainId) => {
			const trainInfo = trainData[trainId];

			// 좌석 행 생성
			const seatRows = trainInfo.rows
				.map((rowNum) => {
					const seatsInRow = [];

					if (isNumericSystem) {
						// 숫자 시스템
						for (let i = 1; i <= 4; i++) {
							const seatKey = `${rowNum * 4 + i}`;
							seatsInRow.push(
								renderSeatForPrint(trainInfo.data[seatKey])
							);
							if (i === 2) {
								seatsInRow.push(
									'<div style="width: 16px;"></div>'
								); // 중앙 공간
							}
						}
					} else {
						// 문자 시스템
						const columns = ["A", "B", "C", "D"];
						columns.forEach((col, index) => {
							const seatKey = `${rowNum}${col}`;
							seatsInRow.push(
								renderSeatForPrint(trainInfo.data[seatKey])
							);
							if (index === 1) {
								seatsInRow.push(
									'<div style="width: 16px;"></div>'
								); // 중앙 공간
							}
						});
					}

					return `
				<div style="display: flex; align-items: center; margin-bottom: 4px;">
					<div style="width: 24px; text-align: center; font-weight: bold; font-size: 10px; margin-right: 6px;">${rowNum}</div>
					${seatsInRow.join("")}
				</div>
			`;
				})
				.join("");

			// 열 헤더 생성
			const columnHeaders = isNumericSystem
				? '<div style="width: 28px; text-align: center; font-weight: bold; font-size: 10px; margin: 2px;">1</div><div style="width: 28px; text-align: center; font-weight: bold; font-size: 10px; margin: 2px;">2</div><div style="width: 16px;"></div><div style="width: 28px; text-align: center; font-weight: bold; font-size: 10px; margin: 2px;">3</div><div style="width: 28px; text-align: center; font-weight: bold; font-size: 10px; margin: 2px;">4</div>'
				: '<div style="width: 28px; text-align: center; font-weight: bold; font-size: 10px; margin: 2px;">A</div><div style="width: 28px; text-align: center; font-weight: bold; font-size: 10px; margin: 2px;">B</div><div style="width: 16px;"></div><div style="width: 28px; text-align: center; font-weight: bold; font-size: 10px; margin: 2px;">C</div><div style="width: 28px; text-align: center; font-weight: bold; font-size: 10px; margin: 2px;">D</div>';

			return `
			<div style="margin-bottom: 0px;">
				<h3 style="text-align: center; margin-bottom: 5px; font-size: 16px; font-weight: bold;">${trainId}호차</h3>
				
				<!-- 좌석 배치도 -->
				<div style="display: flex; justify-content: center;">
					<div>
						<!-- 열 헤더 -->
						<div style="display: flex; align-items: center; margin-bottom: 8px;">
							<div style="width: 32px; margin-right: 6px;"></div>
							${columnHeaders}
						</div>
						
						<!-- 좌석 행들 -->
						${seatRows}
					</div>
				</div>
			</div>
		`;
		});

		// 호차를 2개씩 좌우로 배치
		let trainSeatsHtml = "";
		for (let i = 0; i < trainCarHtmls.length; i += 2) {
			const isNewPage = i > 0;
			const firstCar = trainCarHtmls[i];
			const secondCar =
				i + 1 < trainCarHtmls.length ? trainCarHtmls[i + 1] : "";

			trainSeatsHtml += `
				<div style="page-break-before: ${
					isNewPage ? "always" : "auto"
				}; display: flex; justify-content: space-around; width: 100%;">
					<div style="width: 48%;">
						${firstCar}
					</div>
					${secondCar ? `<div style="width: 48%;">${secondCar}</div>` : ""}
				</div>
			`;
		}

		printContent.innerHTML = `
			<div style="padding: 20px; font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto;">
				<!-- 헤더 정보 -->
				<div style="text-align: center; margin-bottom: 0px; border-bottom: 1px solid #333; padding-bottom: 10px;">
					<h1 style="margin: 0 0 0 0; font-size: 18px; font-weight: bold;">열차 좌석 배치 정보</h1>
					<div style="display: flex; justify-content: center; gap: 30px; margin-top: 8px;">
						<span style="font-size: 12px;"><strong>열차번호:</strong> ${
							ticketDetails?.train_number || "-"
						}</span>
						<span style="font-size: 12px;"><strong>차종:</strong> ${
							resolvedTrainTypeLabel || "-"
						}</span>
						<span style="font-size: 12px;"><strong>출발일자:</strong> ${
							ticketDetails?.departure_date
								? format(
										new Date(ticketDetails.departure_date),
										"yyyy-MM-dd"
								  )
								: "-"
						}</span>
						<span style="font-size: 12px;"><strong>출발역:</strong> ${
							ticketDetails?.departure_station || "-"
						}</span>
						<span style="font-size: 12px;"><strong>도착역:</strong> ${
							ticketDetails?.arrival_station || "-"
						}</span>
					</div>
					<!-- 범례를 헤더 내부로 통합 -->
					<div style="display: flex; justify-content: center; margin-top: 10px; gap: 20px;">
						<div style="display: flex; align-items: center;">
							<div style="width: 12px; height: 12px; background-color: #f9fafb; border: 1px solid #e5e7eb; border-radius: 2px; margin-right: 4px;"></div>
							<span style="font-size: 11px;">빈 좌석</span>
						</div>
						<div style="display: flex; align-items: center;">
							<div style="width: 12px; height: 12px; background-color: white; border: 2px solid #10b981; border-radius: 2px; margin-right: 4px;"></div>
							<span style="font-size: 11px;">배정 가능</span>
						</div>
						<div style="display: flex; align-items: center;">
							<div style="width: 12px; height: 12px; background-color: white; border: 2px solid #3b82f6; border-radius: 2px; margin-right: 4px;"></div>
							<span style="font-size: 11px;">배정 완료</span>
						</div>
					</div>
				</div>

				<!-- 좌석 배치도들 -->
				${trainSeatsHtml}

				<!-- 푸터 -->
				<div style="text-align: center; font-size: 12px; margin-top: 40px; border-top: 1px solid #ccc; padding-top: 20px; color: #666;">
					<p>인쇄일시: ${format(new Date(), "yyyy-MM-dd HH:mm:ss")}</p>
				</div>
			</div>
		`;

		const printWindow = window.open("", "_blank");
		printWindow.document.write(`
			<html>
				<head>
					<title>열차 좌석 배치 정보</title>
					<style>
						@media print {
							body { margin: 0; }
							@page { 
								size: A4; 
								margin: 5mm; 
							}
						}
					</style>
				</head>
				<body>
					${printContent.innerHTML}
					<script>
						window.onload = function() {
							window.print();
							window.setTimeout(function() {
								window.close();
							}, 500);
						};
					</script>
				</body>
			</html>
		`);
		printWindow.document.close();

		// 인쇄 후 모달 닫기
		handleClosePrintModal();
	};

	// 모달이 닫혀있으면 아무것도 렌더링하지 않음
	if (!isOpen) return null;

	// 모달 바깥 영역 클릭 시 닫기 처리
	const handleOverlayClick = (e) => {
		// 클릭된 요소가 오버레이 자체인 경우에만 닫기
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	const hasSeatmapLayout =
		seatmapLayout &&
		Array.isArray(seatmapLayout.cars) &&
		seatmapLayout.cars.length > 0;

	return (
		<div
			className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
			onClick={handleOverlayClick}
		>
			<div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
				{/* 모달 헤더 */}
				<div className="bg-blue-600 text-white p-4 flex items-center justify-between">
					<div className="flex items-center">
						<svg
							xmlns="http://www.w3.org/2000/svg"
							className="h-6 w-6 mr-2"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
							/>
						</svg>
						<h2 className="text-xl font-bold">좌석 배치 상세</h2>
					</div>
					<button
						onClick={onClose}
						className="text-white hover:text-gray-200 focus:outline-none"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							className="h-6 w-6"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</button>
				</div>
				{/* 모달 헤더 */}
				{/* <div className="flex justify-between items-center border-b p-4">
					<h3 className="text-lg font-semibold">좌석 배치 상세</h3>
					<button
						onClick={onClose}
						className="text-gray-500 hover:text-gray-700"
					>
						<XMarkIcon className="h-6 w-6" />
					</button>
				</div> */}

				{/* 로딩 상태 */}
				{isLoading && (
					<div className="flex-1 flex items-center justify-center p-8">
						<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
					</div>
				)}

				{/* 에러 메시지 */}
				{error && !isLoading && (
					<div className="flex-1 flex items-center justify-center p-8">
						<div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
							<p>{error}</p>
						</div>
					</div>
				)}

				{/* 티켓 상세 정보 및 좌석 배치 */}
				{!isLoading && !error && ticketDetails && (
					<div className="flex-1 overflow-auto p-6">
						<div ref={printRef}>
							{/* 티켓 정보 */}
							<div className="bg-gray-50 p-4 rounded-lg mb-6">
								<h3 className="text-lg font-semibold mb-3">
									열차 정보
								</h3>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div>
										<p className="text-gray-600 text-sm">
											열차번호
										</p>
										<p className="font-medium">
											{ticketDetails.train_number}
										</p>
									</div>
									<div>
										<p className="text-gray-600 text-sm">
											차종
										</p>
										<p className="font-medium">
											{resolvedTrainTypeLabel}
										</p>
									</div>
									<div>
										<p className="text-gray-600 text-sm">
											출발일자
										</p>
										<p className="font-medium">
											{format(
												new Date(
													ticketDetails.departure_date
												),
												"yyyy-MM-dd"
											)}
										</p>
									</div>
									<div>
										<p className="text-gray-600 text-sm">
											출발역
										</p>
										<p className="font-medium">
											{ticketDetails.departure_station}
										</p>
									</div>
									<div>
										<p className="text-gray-600 text-sm">
											도착역
										</p>
										<p className="font-medium">
											{ticketDetails.arrival_station}
										</p>
									</div>
								</div>
							</div>

							{/* 좌석 배치 */}
							<h3 className="text-lg font-semibold mb-3">
								좌석 배치
							</h3>
							<div className="mb-6 overflow-x-auto">
								<div className="inline-block min-w-full">
									<div className="bg-gray-100 p-4 rounded-lg mb-4">
										{seatmapLoading && (
											<div className="text-sm text-gray-500 mb-2">
												좌석 배치도 레이아웃 로딩중...
											</div>
										)}
										{seatmapError && !seatmapLoading && (
											<div className="text-sm text-amber-600 mb-2">
												{seatmapError} 기본 그리드로 표시합니다.
											</div>
										)}
										<div className="flex items-center flex-wrap gap-4">
											{hasSeatmapLayout ? (
												<>
													<div className="flex items-center">
														<div className="w-4 h-4 bg-emerald-500 border border-emerald-600 rounded-sm mr-2"></div>
														<span className="text-sm">
															배정 완료 (순)
														</span>
													</div>
													<div className="flex items-center">
														<div className="w-4 h-4 bg-rose-400 border border-rose-500 rounded-sm mr-2"></div>
														<span className="text-sm">
															배정 완료 (역)
														</span>
													</div>
													<div className="flex items-center">
														<div className="w-4 h-4 bg-white border-2 border-emerald-500 rounded-sm mr-2"></div>
														<span className="text-sm">
															배정 가능 (순)
														</span>
													</div>
													<div className="flex items-center">
														<div className="w-4 h-4 bg-white border-2 border-rose-400 rounded-sm mr-2"></div>
														<span className="text-sm">
															배정 가능 (역)
														</span>
													</div>
													<div className="flex items-center">
														<div className="w-4 h-4 bg-gray-100 border border-gray-300 rounded-sm mr-2"></div>
														<span className="text-sm">
															없는 좌석
														</span>
													</div>
												</>
											) : (
												<>
													<div className="flex items-center">
														<div
															className={
																SEAT_STYLES.legend.empty
															}
														></div>
														<span className="text-sm">
															빈 좌석
														</span>
													</div>
													<div className="flex items-center">
														<div
															className={
																SEAT_STYLES.legend
																	.available
															}
														></div>
														<span className="text-sm">
															배정 가능
														</span>
													</div>
													<div className="flex items-center">
														<div
															className={
																SEAT_STYLES.legend
																	.assigned
															}
														></div>
														<span className="text-sm">
															배정 완료
														</span>
													</div>
												</>
											)}
										</div>
									</div>

									{/* 좌석 배치 그리드 - 모든 열차 정보 표시 */}
									<div className="mb-6">
										{/* 열차 정보가 없으면 안내 메시지 표시 */}
										{Object.keys(trainData).length === 0 ? (
											<div className="text-center p-4">
												열차 정보가 없습니다.
											</div>
										) : (
											<div>
												{/* 1열로 표시하여 좌석 정보를 더 크게 */}
												<div className="grid grid-cols-1 gap-4">
													{Object.keys(trainData).map((trainId) =>
														renderSeatmapCar(trainId)
													)}
												</div>
											</div>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
				)}

				{/* 모달 푸터 */}
				<div className="border-t p-4 flex justify-end space-x-3">
					<button
						onClick={handleOpenPrintModal}
						disabled={isLoading}
						className={`flex items-center justify-center px-4 py-2 text-sm font-medium text-white rounded-md ${
							isLoading
								? "bg-gray-400 cursor-not-allowed"
								: "bg-blue-600 hover:bg-blue-700"
						}`}
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							className="h-5 w-5 mr-2"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
							/>
						</svg>
						좌석 정보 인쇄
					</button>
					<button
						onClick={onClose}
						className="border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-100"
					>
						닫기
					</button>
				</div>
			</div>

			{/* 호차 선택 모달 */}
			{showPrintModal && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
					<div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
						{/* 호차 선택 모달 헤더 */}
						<div className="bg-blue-600 text-white p-4 flex items-center justify-between rounded-t-lg">
							<h3 className="text-lg font-bold">
								인쇄할 호차 선택
							</h3>
							<button
								onClick={handleClosePrintModal}
								className="text-white hover:text-gray-200 focus:outline-none"
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									className="h-5 w-5"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M6 18L18 6M6 6l12 12"
									/>
								</svg>
							</button>
						</div>

						{/* 호차 선택 내용 */}
						<div className="p-6">
							<p className="text-gray-600 mb-4">
								인쇄할 호차를 선택해주세요.
							</p>

							{/* 전체 선택/해제 버튼 */}
							<div className="mb-4">
								<button
									onClick={handleSelectAll}
									className="text-blue-600 hover:text-blue-800 text-sm font-medium"
								>
									{selectedTrains.length ===
									Object.keys(trainData).length
										? "전체 해제"
										: "전체 선택"}
								</button>
							</div>

							{/* 호차 목록 */}
							<div className="space-y-2 max-h-60 overflow-y-auto">
								{Object.keys(trainData).map((trainId) => (
									<label
										key={trainId}
										className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
									>
										<input
											type="checkbox"
											checked={selectedTrains.includes(
												trainId
											)}
											onChange={() =>
												handleTrainSelection(trainId)
											}
											className="mr-3 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
										/>
										<div className="flex-1">
											<span className="font-medium">
												{trainId}호차
											</span>
											<span className="text-gray-500 text-sm ml-2">
												(보유 좌석:{" "}
												{trainData[trainId].cnt}석)
											</span>
										</div>
									</label>
								))}
							</div>
						</div>

						{/* 호차 선택 모달 푸터 */}
						<div className="border-t p-4 flex justify-end space-x-3">
							<button
								onClick={handleClosePrintModal}
								className="border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-100"
							>
								취소
							</button>
							<button
								onClick={handlePrint}
								disabled={selectedTrains.length === 0}
								className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
									selectedTrains.length === 0
										? "bg-gray-400 cursor-not-allowed"
										: "bg-blue-600 hover:bg-blue-700"
								}`}
							>
								인쇄 ({selectedTrains.length}개 호차)
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default DailyDetailModal;

// 좌석 상태별 스타일 정의 - 중앙 관리를 위한 상수 객체
export const SEAT_STYLES = {
	empty: {
		container:
			"w-16 h-12 bg-gray-50 border border-gray-200 rounded-md shadow-sm flex items-center justify-center",
		text: "text-gray-300",
	},
	available: {
		container:
			"w-16 h-12 bg-white border-2 border-emerald-400 text-emerald-600 rounded-md shadow-sm flex flex-col items-center justify-center transition-all hover:shadow-md",
		text: "text-xs font-medium",
	},
	assigned: {
		container:
			"w-16 h-12 bg-white border-2 border-blue-500 text-blue-700 rounded-md shadow-sm flex flex-col items-center justify-center transition-all",
		nameText: "text-[10px] font-medium w-full text-center truncate px-0.5",
		countText: "text-[10px] text-blue-500",
	},
	previous: {
		container:
			"w-16 h-12 bg-white border-2 border-red-500 text-red-500 rounded-md shadow-sm flex flex-col items-center justify-center transition-all",
		nameText: "text-[10px] font-medium w-full text-center truncate px-0.5",
		countText: "text-[10px] text-red-500",
	},
	// 범례에 사용할 작은 버전의 스타일
	legend: {
		empty: "w-4 h-4 bg-gray-50 border border-gray-200 rounded-sm mr-2",
		available:
			"w-4 h-4 bg-white border-2 border-emerald-400 text-emerald-600 rounded-sm mr-2",
		assigned:
			"w-4 h-4 bg-white border-2 border-blue-500 text-blue-700 rounded-sm mr-2",
		previous:
			"w-4 h-4 bg-white border-2 border-red-500 text-red-500 rounded-sm mr-2",
	},
};
